import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useUserEmail} from 'src/hooks/useUserEmail';
import {CommentsView} from 'src/modules/comments/comments.view';
import {useCurrentComment} from 'src/modules/comments/hooks/useCurrentComment';
import {formatCommentsDates} from 'src/modules/comments/utils/formatCommentsDates';
import {sortComments} from 'src/modules/comments/utils/sortComments';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getCommentsByChangeRequestId,
    saveComment,
    updateComment,
} from 'src/openapi/apis/ChangeRequestControllerApi';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {ChangeRequestComment} from 'src/openapi/models/ChangeRequestComment';
import {SaveChangeRequestComment} from 'src/openapi/models/SaveChangeRequestComment';
import {UpdateChangeRequestComment} from 'src/openapi/models/UpdateChangeRequestComment';
import {useAppDispatch} from 'src/store';

type Props = {
    changeRequest?: ChangeRequest,
    changeRequestId: number | null,
};

export const CommentsController = ({changeRequest, changeRequestId}: Props) => {
    const dispatch = useAppDispatch();
    const userEmail = useUserEmail();
    const isSaving = useRef(false);
    const [comments, setComments] = useState<ChangeRequestComment[]>([]);
    const {currentComment, updateCurrentComment, clearCommentDraft} = useCurrentComment(changeRequestId);
    const [editedCommentId, setEditedCommentId] = useState<number | null>(null);
    const emailsComments = useMemo(() =>
        [...new Set(comments.map(comment => comment.authorEmail))], [comments]);
    const preparedComments = useMemo(() =>
        comments.length ? formatCommentsDates(sortComments(comments)) : comments, [comments]);

    const updateComments = useCallback(() => {
        if (changeRequestId) {
            const fetchData = async () => {
                const response = await dispatch(getCommentsByChangeRequestId({changeRequestId}));

                if (response.payload.success) {
                    setComments(response.payload.data);
                }
            };

            fetchData();
        }
    }, [changeRequestId, dispatch]);

    useEffect(() => {
        setComments([]);
        updateComments();
    }, [changeRequestId, updateComments]);

    const onAddComment = async () => {
        if (!isSaving.current && changeRequestId && currentComment) {
            isSaving.current = true;

            const saveChangeRequestComment: SaveChangeRequestComment = {
                changeRequestId,
                comment: currentComment,
                authorEmail: userEmail,
                openApiDiscriminator: 'SaveChangeRequestComment',
            };

            const response = await dispatch(saveComment({saveChangeRequestComment}));

            if (response.payload.success) {
                updateComments();
                updateCurrentComment('');
            }

            isSaving.current = false;
        }
    };

    const onChange = (value: string) => {
        updateCurrentComment(value, !editedCommentId);
    };

    const onEditComment = (comment: ChangeRequestComment) => {
        const commentId = comment.id;

        if (commentId) {
            clearCommentDraft();
            updateCurrentComment(comment.comment);
            setEditedCommentId(commentId);
        }
    };

    const onCancel = () => {
        setEditedCommentId(null);
        updateCurrentComment('');
    };

    const onUpdateComment = async () => {
        if (changeRequestId && editedCommentId && currentComment) {
            const updateChangeRequestComment: UpdateChangeRequestComment = {
                id: editedCommentId,
                changeRequestId,
                comment: currentComment,
                authorEmail: userEmail,
                openApiDiscriminator: 'UpdateChangeRequestComment',
            };

            const response = await dispatch(updateComment({updateChangeRequestComment}));

            if (response.payload.success) {
                updateComments();
                updateCurrentComment('');
                setEditedCommentId(null);
            }
        }
    };

    return (
        <>
            <UsersInfoController emails={emailsComments}/>
            <CommentsView {...{
                userEmail,
                changeRequest,
                changeRequestId,
                currentComment,
                comments: preparedComments,
                editedCommentId,
                onAddComment,
                onChange,
                onEditComment,
                onCancel,
                onUpdateComment,
            }}
            />
        </>
    );
};
